import type { Card } from '../game/data/cards/types';
import { TRIBE_COLORS } from '../game/data/tribes';
import type { RailProgress } from './Rail';

// Position indicators for the tavern rails. The rails hide overflow behind
// edge fades with no scrollbar (hidden app-wide), so without these a player
// literally cannot tell that shop card 4 or board slot 6 exists. Two forms:
//
//   <RailDots>     — shop / generic: one dot per item, filled while visible,
//                    plus a "3/5" count.
//   <BoardMinimap> — board: one tappable chip per slot, colored by the
//                    occupying unit's tribe (sodium for goldens, outline for
//                    empty), with the visible window brightened. Tap a chip
//                    to scroll that slot into view. This is the board-state
//                    scannability the audit called out: all 7 slots readable
//                    at a glance even when only 3 fit on screen.

function hexToCss(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`;
}

export function RailDots({
  progress,
  className = '',
}: {
  progress: RailProgress | null;
  className?: string;
}) {
  if (!progress || progress.count <= 1) return null;
  const { firstVisible, lastVisible, count } = progress;
  const allVisible = firstVisible <= 0 && lastVisible >= count - 1;
  if (allVisible) return null;
  return (
    <div
      className={`pointer-events-none flex items-center justify-center gap-[4px] ${className}`}
      aria-hidden
    >
      {Array.from({ length: count }, (_, i) => {
        const visible = i >= firstVisible && i <= lastVisible;
        return (
          <span
            key={i}
            className={`h-[5px] w-[5px] rounded-full transition-colors ${
              visible ? 'bg-sodium' : 'bg-stucco3'
            }`}
          />
        );
      })}
      <span className="ml-[4px] font-data text-[10px] leading-none text-graphite">
        {Math.max(firstVisible, 0) + 1}–{lastVisible + 1}/{count}
      </span>
    </div>
  );
}

export function BoardMinimap({
  board,
  progress,
  onChipTap,
  className = '',
}: {
  board: (Card | null)[];
  progress: RailProgress | null;
  /** Scroll the tapped slot into view. */
  onChipTap: (index: number) => void;
  className?: string;
}) {
  // Unlike the dots, the minimap renders even when everything fits — it
  // doubles as a board-composition summary (tribe colors at a glance).
  // It only hides when the board is empty AND fully visible.
  const allVisible =
    !progress ||
    (progress.firstVisible <= 0 && progress.lastVisible >= progress.count - 1);
  const empty = board.every((s) => s === null);
  if (allVisible && empty) return null;

  return (
    <div
      className={`flex items-center justify-center gap-[5px] ${className}`}
      role="group"
      aria-label="board overview"
    >
      {board.map((card, i) => {
        const inView =
          progress !== null &&
          i >= progress.firstVisible &&
          i <= progress.lastVisible;
        const tribeColor =
          card?.tribe != null ? TRIBE_COLORS[card.tribe] : undefined;
        const fill = card
          ? card.isGolden
            ? '#ff8a3d'
            : tribeColor !== undefined
              ? hexToCss(tribeColor)
              : '#8b8f96'
          : 'transparent';
        return (
          <button
            key={i}
            onClick={() => onChipTap(i)}
            aria-label={
              card ? `scroll to ${card.name} (slot ${i + 1})` : `scroll to empty slot ${i + 1}`
            }
            // Small visual chip inside a much larger hit area. Seven 44px
            // targets would make this strip as tall as a quarter of the
            // board zone, so it lands at 32×40 — below the 44px floor by
            // deliberate exception, because this is a SHORTCUT: the primary
            // way to reach an off-screen slot is swiping the rail itself,
            // which is unaffected.
            className="flex h-[32px] w-[40px] items-center justify-center"
          >
            <span
              className={`h-[11px] w-[11px] rounded-[3px] border transition-opacity ${
                inView ? 'opacity-100' : 'opacity-45'
              }`}
              style={{
                backgroundColor: fill,
                borderColor: card ? 'rgba(0,0,0,0.4)' : 'rgba(139,143,150,0.6)',
                borderStyle: card ? 'solid' : 'dashed',
              }}
            />
          </button>
        );
      })}
    </div>
  );
}
